import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AgentSidebar from "../components/AgentSidebar";

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STAGE_ORDER = [
  "LEAD",
  "AGENT_ASSIGNED",
  "DOCUMENT_COLLECTION",
  "INITIAL_FEE_PAID",
  "SUBMITTED",
  "OFFER_RECEIVED",
  "DEPOSIT_PAID",
  "OFFICIAL_ACCEPTANCE",
  "VISA_APPLICATION",
  "CLOSED",
];
const stageLabel = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AgentApplicationDetailPage() {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [tab, setTab] = useState("personal");
  const [nextStage, setNextStage] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  const token = localStorage.getItem("token");

  const fetchApplication = () => {
    fetch(`${API_BASE}/admin/applications/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setApplication(json.data);
          const idx = STAGE_ORDER.indexOf(json.data.currentStage);
          setNextStage(STAGE_ORDER[idx + 1] || json.data.currentStage);
        }
      });
  };

  useEffect(fetchApplication, [id]);

  const handleStageUpdate = async () => {
    await fetch(`${API_BASE}/admin/applications/${id}/stage`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ toStage: nextStage }),
    });
    fetchApplication();
  };

  const handleReject = async () => {
    await fetch(`${API_BASE}/admin/applications/${id}/reject`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ closeNote: rejectNote }),
    });
    setRejectOpen(false);
    fetchApplication();
  };

  const handleVerifyDoc = async (docId, status) => {
    await fetch(
      `${API_BASE}/admin/applications/${id}/documents/${docId}/verify`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      },
    );
    fetchApplication();
  };

  if (!application)
    return <p className="text-center py-40 text-[#64748b]">Loading...</p>;
  const isClosed = application.currentStage === "CLOSED";

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AgentSidebar />
      <main className="ml-64 min-h-screen p-8">
        <nav className="flex items-center text-sm text-[#64748b] mb-4">
          <Link className="hover:text-[#00255a]" to="/agent/applications">
            My Applications
          </Link>
          <span className="material-symbols-outlined text-[16px] mx-1">
            chevron_right
          </span>
          <span className="text-[#00255a] font-bold">
            {application.applicant?.fullName}
          </span>
        </nav>

        <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
          <div>
            <h2 className="text-3xl font-bold text-[#00255a]">
              {application.applicant?.fullName}
            </h2>
            <p className="text-[#64748b] mt-1">
              {application.opportunity?.title || "General Application"} ·{" "}
              {application.opportunity?.country}
            </p>
          </div>
          {!isClosed && (
            <button
              onClick={() => setRejectOpen(true)}
              className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg font-bold text-sm hover:bg-red-50"
            >
              Reject Application
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
            <div className="flex border-b border-[#e2e8f0] px-6">
              {["personal", "documents"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-6 py-4 font-bold text-sm ${tab === t ? "text-[#00255a] border-b-2 border-[#00255a]" : "text-[#64748b]"}`}
                >
                  {t === "personal" ? "Personal Info" : "Documents"}
                </button>
              ))}
            </div>
            <div className="p-8">
              {tab === "personal" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                  <InfoField
                    label="Email"
                    value={application.applicant?.email}
                  />
                  <InfoField
                    label="WhatsApp/Phone"
                    value={
                      application.applicant?.whatsapp ||
                      application.applicant?.phone
                    }
                  />
                  <InfoField
                    label="Nationality"
                    value={application.applicant?.nationality}
                  />
                  <InfoField
                    label="Passport Number"
                    value={application.formResponses?.passportNumber}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {(application.documents || []).length === 0 ? (
                    <p className="text-[#64748b] text-sm">
                      No documents uploaded yet.
                    </p>
                  ) : (
                    application.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 bg-[#f7f9fb] rounded-lg border border-[#e2e8f0]"
                      >
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-bold text-[#00255a] hover:underline"
                        >
                          {doc.documentType}
                        </a>
                        {doc.status === "VERIFIED" ? (
                          <span className="text-green-600 text-sm font-bold flex items-center gap-1">
                            <span className="material-symbols-outlined text-[18px]">
                              check_circle
                            </span>{" "}
                            Verified
                          </span>
                        ) : (
                          <button
                            onClick={() => handleVerifyDoc(doc.id, "VERIFIED")}
                            className="px-4 py-2 text-sm font-bold text-[#00255a] border border-[#00255a] rounded hover:bg-[#00255a] hover:text-white"
                          >
                            Verify
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4">
            {!isClosed && (
              <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm p-6">
                <h3 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                  Advance Application
                </h3>
                <select
                  value={nextStage}
                  onChange={(e) => setNextStage(e.target.value)}
                  className="w-full bg-[#f7f9fb] border border-[#e2e8f0] rounded-lg px-4 py-3 font-bold text-[#00255a] outline-none mb-4"
                >
                  {STAGE_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {stageLabel(s)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleStageUpdate}
                  className="w-full py-3 bg-[#00255a] text-white font-bold rounded-lg"
                >
                  Update Stage
                </button>
                <p className="text-xs text-[#64748b] mt-3">
                  Approval and final sign-off are handled by an admin.
                </p>
              </div>
            )}
          </div>
        </div>

        {rejectOpen && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setRejectOpen(false)}
          >
            <div
              className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[#00255a] mb-4">
                Reject Application
              </h3>
              <textarea
                rows="4"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Reason..."
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] mb-4 resize-none outline-none"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectOpen(false)}
                  className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-[#64748b] uppercase tracking-widest">
        {label}
      </label>
      <p className="font-bold text-[#00255a]">{value || "—"}</p>
    </div>
  );
}
